import { useState, useEffect, useRef } from 'react';
import { ToastContainer, Slide, Bounce, cssTransition } from "react-toastify";
import './App.css';
import Home from './tabs/Home';
import SettingsPage from './tabs/SettingsPage';
import Narwhal from '@manta-scouting/shared-assets/narwhal-logo.svg'
import Scout from './tabs/Scout';
import Info from './tabs/Info'
import Scanner from './tabs/Scanner';
import Card from './tabs/Card';
import Games from './tabs/Games';
import FrcDeal from './tabs/FrcDeal';
import FrcDealPreload from './components/FrcDealPreload';
import Relay from './tabs/Relay';
import { preloadFieldImage } from './components/Field';
import FieldPng from '@manta-scouting/shared-assets/rebuilt-field.png';

import { ThemeProvider } from "./lib/ThemeContext";
import { AnimationProvider, useAnimation, useEnterAnimation } from "./lib/AnimationContext";
import { useAuth} from "./lib/firebase";
import splash from './assets/styles/splash.module.css'
import { useTBA } from './lib/tba';
import { useOnlineStatus } from './components/Wifi';
import { usePiBackgroundSync } from './components/OctopusStatus';
import UpdateBanner from './components/UpdateBanner';

// Toast transition with an effectively-instant animation, for the "none" animation level.
// (react-toastify waits for animationend, so it still needs a real - if 1ms - animation.)
const NoToastTransition = cssTransition({
    enter: "toastNoAnim",
    exit: "toastNoAnim",
    collapse: false,
});

/**
 * Replays an entrance animation on whichever tab is showing whenever the tab changes.
 * Tabs stay mounted (Scout keeps its in-progress match state), so this only animates, never remounts.
 */
function TabStage({ currentTab, children }) {
    const stageRef = useRef(null);
    useEnterAnimation(stageRef, currentTab, {
        // Cascade the tab's sections in one by one, except on the scouting tab, which handles its own
        // (deliberately lighter) page transitions so nothing gets in a scout's way.
        staggerChildren: currentTab !== "scout",
        childSelector: (el) => Array.from(el.children).flatMap(tabRoot => Array.from(tabRoot.children)),
    });
    return <div ref={stageRef}>{children}</div>;
}

function AppToasts() {
    const { level } = useAnimation();
    const transition = level === "none" ? NoToastTransition : level === "large" ? Bounce : Slide;
    return (
        <ToastContainer
            position="top-right"
            autoClose={1500}
            hideProgressBar={false}
            newestOnTop={false}
            pauseOnFocusLoss={false}
            pauseOnHover
            draggable
            transition={transition}
            // limit={device === "mobile" ? 2 : 4}
        />
    );
}

function App() {
    const { user, authentication, setUser, setAuthentication } = useAuth();
    const [currentTab, setCurrentTab] = useState("");
    // The Scout tab's own page ("start", auto, tele, Post, Upload, timekeep): the update banner waits out a match
    const [scoutPage, setScoutPage] = useState("start");
    const [funnySounds, setFunnySounds] = useState(localStorage.getItem("funnyStatus") === "true");
    const [sfxEnabled, setSfxEnabled] = useState(localStorage.getItem("sfxStatus") === "true");
    const defaultEventList = new Map([
        ['2026casnd', 'San Diego'],
        ['2026capoh', 'Hueneme Port'],
        ['2026cascmp', 'SoCal DChamps'],
        ['2026casan1', 'Stay Classy']
    ]);
    const [eventList,setEventList] = useState(new Map(defaultEventList));
    const [specialUpload, setSpecialUpload] = useState(false)
    const online = useOnlineStatus();
    const [gamemode, setGamemode] = useState(2);
    const {eventPath, setEventPath, teamList, tbaOnline, tbaStatus, refresh, robotNameList, tbaEventOverride, setTbaEventOverride} = useTBA(gamemode);
    // Waiting records go to the Pi from every screen, not only where its status chip shows
    usePiBackgroundSync(eventPath);
    const [settingsYear, setSettingsYear] = useState((new Date(Date.now())).getFullYear());

    /**
     * false is accuracy mode and true is count mode for misses
     */
    const [rebuiltMissType, setRebuiltMissType] = useState(false);

    useEffect(() => {
        if (currentTab === "" || currentTab === "signin") {
            setCurrentTab("home");
        }
    }, [currentTab]);

    /**
     * Start loading the auto-field drawing image in the background as soon as the app opens,
     * instead of waiting for the user to open the field popup while scouting.
     */
    useEffect(() => {
        preloadFieldImage(FieldPng);
    }, []);
    
    const [dataMap, setDataMap] = useState(new Map()); //the actual data ****

    const [initialDataMap, setInitialDataMap] = useState(new Map()) //the initial stuff like event path and the like
    const [scoutDataMap, setScoutDataMap] = useState(new Map()) //the data map that is only used for scouting and is reset after every match

    const [loading, setLoading] = useState(true);

    /**
     * Reload protection
     */
    useEffect(() => {
        const handler = (e) => {
            e.preventDefault();

        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, []);
    
    /**
     * Splash screen for 3 seconds
     */
    useEffect(() => {
        const timer = setTimeout(() => {
            setLoading(false);
        }, 3000);

        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        const lockPortrait = async () => {
            if (window.screen.orientation && window.screen.orientation.lock) {
                try {
                    await window.screen.orientation.lock("portrait");
                } catch (err) {
                    console.warn("Orientation lock failed:", err);
                }
            }
        };
        lockPortrait();
    }, []);

    useEffect(() => {
        const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
        const isStandalone = window.navigator.standalone === true;

        if (isIOS && isStandalone) {
            const reapplyLocks = () => {
                document.body.style.overscrollBehavior = "none";
                document.body.style.overscrollBehaviorX = "contain";
            };

            window.addEventListener("focus", reapplyLocks);
            document.addEventListener("visibilitychange", () => {
                if (!document.hidden) reapplyLocks();
            });

            reapplyLocks();

            return () => {
            window.removeEventListener("focus", reapplyLocks);
            document.removeEventListener("visibilitychange", reapplyLocks);
            };
        }
    }, []);
    
    if (loading) {
        return (
        <div className={splash.splashScreen}>
            <div className={splash.particles}>
                {[...Array(15)].map((_, i) => (
                    <span key={i}></span>
                ))}
            </div>
            <img className={splash.logo} src={Narwhal} alt="Narwhal Logo"/>
            <h1 className={splash.title}>Osprey</h1>
            <p className={splash.subtitle}>Presented by Team 3128 — The Aluminum Narwhals</p>
        </div>
        );
    }

    return (
        <div className="App">
            <>
            <ThemeProvider>
            <AnimationProvider>
                <FrcDealPreload online={online} />
                <TabStage currentTab={currentTab}>
                <Home
                    currentTab={currentTab}
                    setUser={setUser}
                    user={user}
                    setCurrentTab={setCurrentTab}
                    authentication={authentication}
                    setAuthentication={setAuthentication}
                    setInitialDataMap ={setInitialDataMap}
                    initialDataMap={initialDataMap}
                    eventPath={eventPath}
                    setEventPath={setEventPath}
                    funnySounds={funnySounds}
                    sfxEnabled={sfxEnabled}
                    eventList={eventList}
                    setEventList={setEventList}
                    settingsYear={settingsYear}
                    gamemode={gamemode}
                    setGameMode={setGamemode}
                />
                <Scout 
                    currentTab={currentTab}
                    setCurrentTab={setCurrentTab}
                    dataMap={dataMap}
                    setDataMap={setDataMap}
                    setInitialDataMap={setInitialDataMap}
                    initialDataMap={initialDataMap}
                    setScoutDataMap={setScoutDataMap}
                    scoutDataMap={scoutDataMap}
                    eventPath={eventPath}
                    tbaOnline={tbaOnline}
                    tbaStatus={tbaStatus}
                    teamList={teamList}
                    funnySounds={funnySounds}
                    sfxEnabled={sfxEnabled}
                    eventList={eventList}
                    onlineStatus={online}
                    settingsYear={settingsYear}
                    gamemode={gamemode}
                    setGameMode={setGamemode}
                    rebuiltMissType={rebuiltMissType}
                    setRebuiltMissType={setRebuiltMissType}
                    refresh={refresh}
                    specialUpload={specialUpload}
                    onPageChange={setScoutPage}
                />
                <SettingsPage
                    currentTab={currentTab}
                    setCurrentTab={setCurrentTab}
                    eventPath={eventPath}
                    tbaEventOverride={tbaEventOverride}
                    setTbaEventOverride={setTbaEventOverride}
                    eventList={eventList}
                    refresh={refresh}
                    funnySounds={funnySounds}
                    setFunnySounds={setFunnySounds}
                    sfxEnabled={sfxEnabled}
                    setSfxEnabled={setSfxEnabled}
                    online={online}
                    setSettingsYear={setSettingsYear}
                    settingsYear={settingsYear}
                    gamemode={gamemode}
                    setGamemode={setGamemode}
                    setRebuiltMissType={setRebuiltMissType}
                    rebuiltMissType={rebuiltMissType}
                    specialUpload={specialUpload}
                    setSpecialUpload={setSpecialUpload}
                />
                <Info 
                    currentTab={currentTab}
                    setCurrentTab={setCurrentTab}
                    configYear = {settingsYear}
                />
                <Scanner 
                    currentTab={currentTab}
                    setCurrentTab={setCurrentTab}
                    eventPath={eventPath}
                    eventList={eventList}
                    funnySounds={funnySounds}
                    sfxEnabled={sfxEnabled}
                    onlineStatus={online}
                    settingsYear={settingsYear}
                    gamemode={gamemode}
                />
                <Card
                    currentTab={currentTab}
                    setCurrentTab={setCurrentTab}
                    eventPath={eventPath}
                    robotNameList={robotNameList}
                    funnySounds={funnySounds}
                    sfxEnabled={sfxEnabled}
                />
                <Games
                    currentTab={currentTab}
                    setCurrentTab={setCurrentTab}
                />
                <FrcDeal
                    currentTab={currentTab}
                    setCurrentTab={setCurrentTab}
                    onlineStatus={online}
                />
                <Relay
                    currentTab={currentTab}
                    setCurrentTab={setCurrentTab}
                    eventPath={eventPath}
                    settingsYear={settingsYear}
                    funnySounds={funnySounds}
                    sfxEnabled={sfxEnabled}
                    onlineStatus={online}
                />
                </TabStage>
                <UpdateBanner scouting={currentTab === "scout" && scoutPage !== "start"} />
                <AppToasts />
            </AnimationProvider>
            </ThemeProvider>
            </>
        </div>
    );
}

export default App;
